import React, {useEffect, useMemo, useState} from 'react';
import {Chip, CircularProgress, Stack, Typography} from '@mui/material';
import {apiSuggestDataItemSegmentTags, apiSuggestTagsBySegmentId, apiSuggestTagsByText} from "../../utils/api/tags";
import {getTagColor} from './tagColors';
import {useSession} from '../../firebase/UserProvider';

/**
 * SuggestedTags
 * Props:
 * - segmentId: corpus segment id; when present, suggestions are fetched per-segment.
 * - dataItemSegmentId: My Data sentence-segment id; when present, suggestions come from the
 *   DiTag suggest endpoint (POST /api/data-items/segments/{id}/tags/suggest) instead.
 * - contentText: snippet text; used as a fallback when segmentId is absent (report /
 *   data-item source cards have no corpus segmentId) so they still get suggestions.
 * - onAddExisting: (name) => void
 * - onAddNew: (name) => void
 * - disabled: boolean
 * - currentTags: array of tags already present on the segment (objects or strings) — these will be excluded from suggestions
 */
export default function SuggestedTags({
                                          segmentId,
                                          dataItemSegmentId,
                                          contentText,
                                          onAddExisting,
                                          onAddNew,
                                          disabled,
                                          currentTags
                                      }) {
    // Null-safe: useSession() is undefined outside UserProvider (e.g. unit tests).
    const session = useSession();
    const userId = session?.user?.uid || null;

    const [suggestedTags, setSuggestedTags] = useState()
    const [isLoading, setIsLoading] = useState(true);

    const {existingTags, newTags} = suggestedTags || {};

    const ex = useMemo(() => (Array.isArray(existingTags) ? existingTags : []), [existingTags]);
    const nw = useMemo(() => (Array.isArray(newTags) ? newTags : []), [newTags]);

    useEffect(() => {
        let cancelled = false;
        const text = (contentText || '').toString().trim();

        // No segment locator and no text to analyze → nothing to suggest.
        if (!segmentId && !dataItemSegmentId && !text) {
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);

        // Prefer per-segment analysis: corpus segments use the corpus suggest endpoint;
        // My Data sentence segments use the DiTag suggest endpoint. Report-segment /
        // highlight cards (no segment locator) fall back to text-based analysis.
        const request = segmentId
            ? apiSuggestTagsBySegmentId({segmentId}).then(response => {
                const {existingTags: e = [], newTags: n = []} = response?.segments?.[0] || {};
                return {existingTags: e, newTags: n};
            })
            : dataItemSegmentId
                ? apiSuggestDataItemSegmentTags({segmentId: dataItemSegmentId, userId})
                : apiSuggestTagsByText({text});

        request
            .then(({existingTags: e = [], newTags: n = []}) => {
                if (cancelled) return;
                setSuggestedTags({existingTags: e, newTags: n});
            })
            .catch(error => {
                if (cancelled) return;
                console.error('Error fetching suggested tags:', error);
                setSuggestedTags({existingTags: [], newTags: []});
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [segmentId, dataItemSegmentId, userId, contentText]);

    // Normalize current segment tags into a Set of lowercase names so we can filter suggested tags
    const existingTagNames = useMemo(() => {
        const set = new Set();
        if (!Array.isArray(currentTags)) return set;
        for (const t of currentTags) {
            try {
                const raw = (typeof t === 'string') ? t : (t?.name || t?.tag || '');
                const n = String(raw).trim().toLowerCase();
                if (n) set.add(n);
            } catch (e) {
                // ignore
            }
        }
        return set;
    }, [currentTags]);

    // Filter suggestions to exclude any tag names that already exist on the segment
    const filteredExisting = useMemo(() => ex.filter(t => {
        const label = (t?.name || t?.tag || '').toString().trim();
        if (!label) return false;
        return !existingTagNames.has(label.toLowerCase());
    }), [ex, existingTagNames]);

    const filteredNew = useMemo(() => nw.filter(t => {
        const label = (t?.name || t?.tag || '').toString().trim();
        if (!label) return false;
        return !existingTagNames.has(label.toLowerCase());
    }), [nw, existingTagNames]);

    return (
        <Stack spacing={0.5} sx={{mt: 0.5}}>

            {isLoading ?
                <Stack direction={'row'} spacing={1} alignItems="center">
                    <Typography
                        variant="caption"
                        fontStyle="italic"
                        color="text.secondary"
                    >
                        Suggesting tags
                    </Typography>
                    <CircularProgress
                        size="12px"
                        disableShrink
                        sx={{
                            animationDuration: '375ms',
                        }}
                    />
                </Stack>
                :
                filteredExisting.length > 0 || filteredNew.length > 0 ?
                    (
                        <>
                            <Typography variant="caption" color="text.secondary">Suggested tags</Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {filteredExisting.map((t, i) => {
                                    const label = (t?.name || t?.tag || '').toString();
                                    if (!label) return null;
                                    return (
                                        <Chip
                                            key={`ex-${i}-${label}`}
                                            label={label}
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (disabled) return;
                                                if (typeof onAddExisting === 'function') onAddExisting(label);
                                            }}
                                            variant="outlined"
                                            sx={{
                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                borderColor: getTagColor(t),
                                                color: getTagColor(t),
                                                '&:hover': {bgcolor: `${getTagColor(t)}18`},
                                            }}
                                        />
                                    );
                                })}
                                {/* New tags */}
                                {filteredNew.length > 0 &&
                                    filteredNew.map((t, i) => {
                                        const label = (t?.name || t?.tag || '').toString();
                                        if (!label) return null;
                                        return (
                                            <Chip
                                                key={`new-${i}-${label}`}
                                                label={label}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (disabled) return;
                                                    if (typeof onAddNew === 'function') onAddNew(label);
                                                }}
                                                variant="outlined"
                                                sx={{
                                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                                    borderColor: getTagColor(t),
                                                    color: getTagColor(t),
                                                    '&:hover': {bgcolor: `${getTagColor(t)}18`},
                                                }}
                                            />
                                        );
                                    })}
                            </Stack>
                        </>
                    )
                    :
                    <Typography variant="caption" color="text.secondary">No suggested tags</Typography>
            }

        </Stack>)
}
